/* eslint-disable @typescript-eslint/no-explicit-any */
import builder from '@builder.io/react'

import type { GetServerSidePropsContext } from 'next'

import type { ProductKeys } from '@/legacy/components/Products/constants'
import { BUILDER_BLOG_MODEL } from '@/legacy/config/builder'
import {
  DEFAULT_ARTICLES_PER_PAGE,
  HIDE_FROM_LIST,
  LATEST_ARTICLES_SORT,
  MOST_VIEWED_SORT,
  PICKED_FIELDS,
  QUERY_OTHER_ARTICLES,
  RELATED_PICKED_FIELDS,
} from '@/legacy/core/blog/constants'
import type { CardArticleEntryData, Category } from '@/legacy/models'

interface GetBuilderPageProps extends Pick<GetServerSidePropsContext, 'req' | 'res'> {
  product?: ProductKeys
  categoryName?: Category['name']
  slug?: string
}

export interface GetArticlesProps extends GetBuilderPageProps {
  limit?: number
  query?: {
    [k in string]: any
  }
  queryOthersArticles?: {
    [k in string]: any
  }
}

export const getArticlesQuery = (
  product?: ProductKeys,
  shouldIncludeHiddenFromBlogHome = false,
) => ({
  ...(product && { 'data.product': product }),
  ...(!shouldIncludeHiddenFromBlogHome && HIDE_FROM_LIST),
})

export const getBuilderArticlesProps = ({
  req,
  res,
  product,
}: GetBuilderPageProps): Promise<CardArticleEntryData[]>[] => {
  const query = getArticlesQuery(product)

  return [
    getViewedArticles({ req, res, query }),
    getLatestArticles({ req, res, query }),
    getOthersArticles({ req, res, product }),
  ]
}

export const getViewedArticles = async ({ req, res, query }: GetArticlesProps) =>
  builder.getAll(BUILDER_BLOG_MODEL, {
    res,
    req,
    options: {
      includeRefs: true,
      noTargeting: true,
      sort: MOST_VIEWED_SORT,
    },
    query,
    fields: PICKED_FIELDS,
    limit: DEFAULT_ARTICLES_PER_PAGE + 1,
  }) as Promise<CardArticleEntryData[]>

export const getLatestArticles = async ({ req, res, query }: GetArticlesProps) =>
  builder.getAll(BUILDER_BLOG_MODEL, {
    res,
    req,
    options: {
      includeRefs: true,
      noTargeting: true,
      sort: LATEST_ARTICLES_SORT,
    },
    query,
    fields: PICKED_FIELDS,
    limit: DEFAULT_ARTICLES_PER_PAGE + 1,
  }) as Promise<CardArticleEntryData[]>

export const getOthersArticles = async ({ req, res, product }: GetArticlesProps) =>
  !product
    ? (builder.getAll(BUILDER_BLOG_MODEL, {
        res,
        req,
        options: {
          includeRefs: true,
          noTargeting: true,
          sort: LATEST_ARTICLES_SORT,
        },
        query: QUERY_OTHER_ARTICLES,
        fields: PICKED_FIELDS,
        limit: 7,
      }) as Promise<CardArticleEntryData[]>)
    : []

const getArticlesWithoutCurrentSlugQuery = (slug?: string) => ({ 'data.slug': { $ne: slug } })

const getRelatedArticlesQueries = ({
  product,
  slug,
}: Partial<{
  product: ProductKeys
  slug: string
}>) => ({
  ...getArticlesWithoutCurrentSlugQuery(slug),
  ...getArticlesQuery(product),
})

export const getRelatedArticles = async ({
  req,
  res,
  limit = 3,
  product,
  slug,
}: GetArticlesProps) =>
  builder.getAll(BUILDER_BLOG_MODEL, {
    res,
    req,
    options: {
      includeRefs: true,
      noTargeting: true,
    },
    query: getRelatedArticlesQueries({ product, slug }),
    fields: RELATED_PICKED_FIELDS,
    limit,
  }) as Promise<CardArticleEntryData[]>
